import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { getUserServiceBrowserUrl } from "../lib/api/userServiceBaseUrl";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { useI18n } from "../lib/i18n";
import * as s from "../styles/dev-captures.css";

type CapturedSms = {
  to: string;
  body: string;
  meta?: Record<string, unknown>;
  sentAt: string;
};

export const Route = createFileRoute("/captured-sms")({
  component: CapturedSmsPage,
});

function CapturedSmsPage() {
  const { t } = useI18n();
  const [messages, setMessages] = useState<CapturedSms[]>([]);
  const [selected, setSelected] = useState<CapturedSms | null>(null);
  const [loading, setLoading] = useState(true);

  const baseUrl = getUserServiceBrowserUrl();

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${baseUrl}/dev/captured-sms`);
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.sms ?? []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleClear = async () => {
    await fetch(`${baseUrl}/dev/captured-sms`, { method: "DELETE" });
    setMessages([]);
    setSelected(null);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const truncateBody = (body: string, max = 60) =>
    body.length > max ? body.slice(0, max) + "…" : body;

  return (
    <div className={s.page}>
      <div className={s.topBar}>
        <span className={s.topBarTitle}>{t("pro.devCaptures.title")}</span>
        <nav className={s.tabNav}>
          <Link to="/captured-emails" className={s.tabLink}>
            {t("pro.devCaptures.emails")}
          </Link>
          <Link to="/captured-sms" className={`${s.tabLink} ${s.tabLinkActive}`}>
            {t("pro.devCaptures.sms")}
          </Link>
        </nav>
        <div className={s.topBarActions}>
          <DarkModeToggle placement="header" />
        </div>
      </div>

      <div className={s.body}>
        <div className={s.sidebar}>
          <div className={s.sidebarHeader}>
            <span className={s.sidebarCount}>
              {t("pro.devCaptures.messageCount", { count: messages.length })}
            </span>
            <div className={s.sidebarActions}>
              <button type="button" className={s.refreshBtn} onClick={fetchMessages}>
                {t("pro.devCaptures.refresh")}
              </button>
              {messages.length > 0 && (
                <button type="button" className={s.clearBtn} onClick={handleClear}>
                  {t("pro.devCaptures.clearAll")}
                </button>
              )}
            </div>
          </div>

          {loading && messages.length === 0 && (
            <div className={s.emptyState}>{t("pro.devCaptures.loading")}</div>
          )}

          {!loading && messages.length === 0 && (
            <div className={s.emptyState}>
              <span>{t("pro.devCaptures.noSms")}</span>
              <span style={{ fontSize: 12 }}>
                {t("pro.devCaptures.noSmsHint")}
              </span>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={`${msg.sentAt}-${idx}`}
              className={`${s.listItem} ${selected === msg ? s.listItemActive : ""}`}
              onClick={() => setSelected(msg)}
            >
              <div className={s.listItemTo}>{formatPhone(msg.to)}</div>
              <div className={s.listItemSubject}>{truncateBody(msg.body)}</div>
              <div className={s.listItemTime}>{formatTime(msg.sentAt)}</div>
            </div>
          ))}
        </div>

        {!selected ? (
          <div className={s.detailEmpty}>{t("pro.devCaptures.selectMessage")}</div>
        ) : (
          <div className={s.detail}>
            <div className={s.detailHeader}>
              <div className={s.detailMeta}>
                <span className={s.detailMetaLabel}>{t("pro.devCaptures.to")}</span>
                {formatPhone(selected.to)}
              </div>
              <div className={s.detailMeta}>
                <span className={s.detailMetaLabel}>{t("pro.devCaptures.sent")}</span>
                {formatTime(selected.sentAt)}
              </div>
              {selected.meta && (
                <div className={s.detailMeta}>
                  <span className={s.detailMetaLabel}>{t("pro.devCaptures.meta")}</span>
                  {JSON.stringify(selected.meta)}
                </div>
              )}
            </div>

            <div className={s.detailText}>{selected.body}</div>
          </div>
        )}
      </div>
    </div>
  );
}
